import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';

const PricingSection = ({ id }) => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const handleBillingChange = (event, newBilling) => {
    if (newBilling !== null) {
      setBillingCycle(newBilling);
    }
  };

  const plan = {
    name: 'Scientific Portfolio Guidance',
    monthlyPrice: '9',
    annualPrice: '90',
    features: [
      'Score your portfolio anytime',
      'Get detailed improvement recommendations',
      'Access to Nobel Prize-winning portfolio frameworks',
      'Weekly performance insights',
      'Email notifications for portfolio improvements',
      'Unlimited portfolio updates',
    ],
    buttonText: 'Get Started',
  };

  const PlanFeature = ({ feature }) => (
    <ListItem disableGutters sx={{ py: 1 }}>
      <ListItemIcon sx={{ minWidth: 40 }}>
        <CheckIcon color="primary" />
      </ListItemIcon>
      <ListItemText 
        primary={feature} 
        sx={{ 
          '& .MuiListItemText-primary': {
            fontSize: '0.95rem',
          }
        }} 
      />
    </ListItem>
  );

  return (
    <Box
      id={id}
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: 'background.paper',
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h2"
          align="center"
          sx={{
            mb: 2,
            fontWeight: 'bold',
          }}
        >
          Start Optimizing Your Portfolio
        </Typography>

        <Typography
          variant="h5"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}
        >
          Get scientific guidance for less than the cost of a single trade
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <ToggleButtonGroup
            value={billingCycle}
            exclusive
            onChange={handleBillingChange}
            aria-label="billing cycle"
            size="large"
          >
            <ToggleButton value="monthly">
              Monthly
            </ToggleButton>
            <ToggleButton value="annual">
              Annual (Save 17%)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Paper
          elevation={4}
          sx={{
            maxWidth: 600,
            mx: 'auto',
            p: 4,
            borderRadius: 4,
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-8px)',
            },
            border: 2,
            borderColor: 'primary.main',
          }}
        >
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h3" gutterBottom fontWeight="bold">
              {plan.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 2 }}>
              <Typography variant="h3" component="span" fontWeight="bold">
                ${billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice}
              </Typography>
              <Typography variant="h6" component="span" color="text.secondary" sx={{ ml: 1 }}>
                /{billingCycle === 'monthly' ? 'month' : 'year'}
              </Typography>
            </Box>
            {billingCycle === 'annual' && (
              <Typography variant="subtitle1" color="success.main" sx={{ fontWeight: 'medium' }}>
                Save $18 with annual billing
              </Typography>
            )}
          </Box>

          <List sx={{ mb: 4 }}>
            {plan.features.map((feature, index) => (
              <PlanFeature key={index} feature={feature} />
            ))}
          </List>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate('/signup/premium')}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
            }}
          >
            {plan.buttonText}
          </Button>
        </Paper>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            30-day money-back guarantee • No long-term contracts • Cancel anytime
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PricingSection;